import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Loader, Typography } from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { getPreLaunchDetail } from '../../../redux/prelaunch/action';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';

import './PreLaunchDetail.css';

const PAGE_SIZE = 50;

const DATE_COL_PATTERN = /(_date|_at|date|time)$/i;

const HIDDEN_COLS = /^(indexed_at|metric_type|.*_id|.*_key|id|key)$/i;

const DATE_FORMAT = 'MMM DD, YYYY hh:mm:ss';

function formatCellValue(col, value) {
  if (value == null || value === '') return '-';

  if (DATE_COL_PATTERN.test(col)) {
    const m = moment.utc(value).local();

    if (m.isValid()) return m.format(DATE_FORMAT);
  }

  return value;
}

function PreLaunchDetail({ metric, page, onPaginationReady }) {
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const { detailData, isDetailLoading, detailError, reportData } = useSelector(
    (state) => state.certifications.prelaunch
  );

  useEffect(() => {
    if (!reportData?.reportId || !metric) return;
    dispatch(
      getPreLaunchDetail({
        reportId: reportData.reportId,
        metric,
        page,
        size: PAGE_SIZE,
      })
    );
  }, [dispatch, reportData?.reportId, metric, page]);

  const records = detailData?.records || [];
  // cursor path returns total=-1 on pages >1 (skip re-count); fall back to cached value via prop
  const rawTotal = detailData?.total ?? 0;
  const totalCount = rawTotal >= 0 ? rawTotal : 0;
  const totalPages =
    detailData?.totalPages ||
    (totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 0);
  // cursor path has no `page` field; use the prop directly
  const currentPage = detailData?.page ?? page;

  useEffect(() => {
    if (onPaginationReady) {
      onPaginationReady({ currentPage, totalPages, totalCount });
    }
  }, [currentPage, totalPages, totalCount]); // eslint-disable-line

  // Use server-provided column labels (getPreLaunchDetail returns columnLabels map)
  const columnLabels = detailData?.columnLabels || {};
  const columns =
    records.length > 0
      ? Object.keys(records[0]).filter((col) => !HIDDEN_COLS.test(col))
      : [];
  const formatColLabel = (col) => columnLabels[col] || col.replace(/_/g, ' ');

  return (
    <Box className='PreLaunchDetail'>
      {isDetailLoading && (
        <Box className='PreLaunchDetail-loading'>
          <Loader kind='dots' format='inline' size='medium' color='brand' />
          {l('Loading…')}
        </Box>
      )}

      {!isDetailLoading && detailError && (
        <Box className='PreLaunchDetail-errorState'>
          <Typography kind='body2'>{detailError}</Typography>
        </Box>
      )}

      {!isDetailLoading && !detailError && records.length === 0 && (
        <Box className='PreLaunchDetail-empty'>{l('No records found.')}</Box>
      )}

      {!isDetailLoading && !detailError && records.length > 0 && (
        <Box className='PreLaunchDetail-tableWrap'>
          <table className='PreLaunchDetail-table'>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{formatColLabel(col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((row, rowIdx) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIdx}>
                  {columns.map((col) => (
                    <td key={col}>{formatCellValue(col, row[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
}

PreLaunchDetail.propTypes = {
  metric: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  onPaginationReady: PropTypes.func,
};

PreLaunchDetail.defaultProps = {
  onPaginationReady: null,
};

export default PreLaunchDetail;
